import { getGameAssetUrl } from './assets';

/**
 * Tournaments feature — shared types, the game catalogue, and display helpers.
 *
 * Live tournament data is read from the public `tournaments` table via
 * `lib/tournaments-data.ts`, which maps DB rows to the `Tournament` shape
 * defined here. These helpers are pure and used by both server and client.
 */

export type TournamentStatus = 'upcoming' | 'live' | 'full' | 'completed';

/** Listing visibility. 'private' = link-only, optionally password protected. */
export type TournamentVisibility = 'public' | 'private';

export type TournamentPlatform =
  | 'PC'
  | 'PlayStation'
  | 'Xbox'
  | 'Mobile'
  | 'Cross-platform';

export type TournamentFormat =
  | '1v1'
  | '2v2'
  | '3v3'
  | '5v5'
  | 'Battle Royale'
  | 'Swiss'
  | 'Single Elimination'
  | 'Double Elimination'
  | 'Round Robin'
  // Battle-royale team modes (Fortnite uses these names natively).
  | 'Solo'
  | 'Duos'
  | 'Trios'
  | 'Squads';

export interface ScheduleItem {
  /** Short label, e.g. "Day 1 — Group Stage". */
  label: string;
  /** ISO datetime for the phase start. */
  start: string;
  description?: string;
}

/** A user linked to a tournament as an organizer/host. */
export interface TournamentOrganizer {
  id: string;
  gamertag: string;
  avatar?: string | null;
}

export interface Tournament {
  id: string;
  slug: string;
  title: string;
  /** Display name of the game, e.g. "Apex Legends". */
  game: string;
  gameSlug: string;
  /** Optional wide banner/cover image URL. Falls back to game icon + gradient. */
  coverImage?: string | null;
  /** Accent gradient (from/to) used when no cover image is present. */
  accent: { from: string; to: string };

  startsAt: string; // ISO
  timezone: string; // e.g. "UTC", "EST"
  registrationDeadline: string; // ISO

  region: string; // e.g. "North America", "Europe", "Global"
  eligibility?: string;
  platform: TournamentPlatform;

  entryFeeCents: number; // 0 == Free
  prizePoolCents: number;
  currency: string; // e.g. "USD"

  /** Number currently registered (players or teams depending on format). */
  registered: number;
  maxParticipants: number;
  /** Players per team. 1 for solo formats. */
  teamSize: number;
  format: TournamentFormat;

  status: TournamentStatus;

  organizer: {
    name: string;
    verified: boolean;
    avatar?: string | null;
  };

  /**
   * Linked organizer users (from `tournament_organizers`), ordered for display.
   * When present these are the real creators hosting the event; the `organizer`
   * block above is the fallback label when no users are linked.
   */
  organizers: TournamentOrganizer[];

  /**
   * Slug of the `organizations` row behind `organizer`, when this event was
   * imported and its organizer resolved to a profile at /org/<slug>. Null for
   * GamerPlug-native events, whose organizers are users with their own
   * profiles (see `organizers` above).
   */
  organizationSlug?: string | null;

  description: string;
  rules: string[];
  requirements: string[];
  schedule: ScheduleItem[];

  bracketUrl?: string | null;
  discordUrl?: string | null;
  externalRegistrationUrl?: string | null;
  /** Overstat match id the results page pulls standings from (set by organizers). */
  overstatMatchId?: string | null;
  /** Organizer's livestream link (Twitch/YouTube), embedded while live. */
  streamUrl?: string | null;

  /**
   * External provider this event was imported from (e.g. 'challengermode').
   * Undefined for native Gamerplug events. Used to style/filter externally
   * sourced events differently in the list.
   */
  source?: string | null;

  /**
   * 'private' events are link-only: hidden from the tournaments list, the
   * personal dashboards and the sitemap, and unreadable via RLS unless you
   * organize or are registered for them. Public is the default.
   */
  visibility?: TournamentVisibility;

  /**
   * True when a private event has a password set. Only the flag travels to the
   * client — the bcrypt hash never leaves the database.
   */
  hasPassword?: boolean;
}

/* ------------------------------------------------------------------ */
/* Display helpers                                                     */
/* ------------------------------------------------------------------ */

export const STATUS_META: Record<
  TournamentStatus,
  { label: string; dot: string; badge: string; text: string }
> = {
  upcoming: {
    label: 'Upcoming',
    dot: 'bg-sky-400',
    badge: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
    text: 'text-sky-300',
  },
  live: {
    label: 'Live',
    dot: 'bg-emerald-400',
    badge: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    text: 'text-emerald-300',
  },
  full: {
    label: 'Full',
    dot: 'bg-amber-400',
    badge: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    text: 'text-amber-300',
  },
  completed: {
    label: 'Completed',
    dot: 'bg-zinc-400',
    badge: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30',
    text: 'text-zinc-300',
  },
};

export function formatMoney(cents: number, currency = 'USD'): string {
  if (cents === 0) return 'Free';
  const amount = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);
}

export function formatPrizePool(cents: number, currency = 'USD'): string {
  const amount = cents / 100;
  if (amount >= 1000) {
    const k = amount / 1000;
    return `$${k % 1 === 0 ? k : k.toFixed(1)}K`;
  }
  return formatMoney(cents, currency);
}

/**
 * Map a stored timezone label (e.g. "ET") to an IANA zone so dates render in
 * the event's zone, not the viewer's. Falls back to treating the label as an
 * IANA id (e.g. "UTC", "America/New_York") if it isn't a known abbreviation.
 */
const TZ_LABEL_TO_IANA: Record<string, string> = {
  ET: 'America/New_York',
  EST: 'America/New_York',
  EDT: 'America/New_York',
  CT: 'America/Chicago',
  CST: 'America/Chicago',
  CDT: 'America/Chicago',
  MT: 'America/Denver',
  PT: 'America/Los_Angeles',
  PST: 'America/Los_Angeles',
  PDT: 'America/Los_Angeles',
  UTC: 'UTC',
  GMT: 'UTC',
};

/** Memoised probe results so we only ask Intl about each label once. */
const zoneValidity = new Map<string, string>();

/**
 * Resolve an arbitrary label to a usable IANA zone id. Known abbreviations map
 * directly; anything else is treated as an IANA id but *verified* against Intl
 * first, because `new Intl.DateTimeFormat({ timeZone })` throws `RangeError` on
 * unknown values (e.g. ingested "CEST"/"BST" labels) — which would otherwise
 * crash any page formatting that date.
 */
export function ianaZone(timezone?: string): string {
  if (!timezone) return 'UTC';
  const mapped = TZ_LABEL_TO_IANA[timezone];
  if (mapped) return mapped;

  const cached = zoneValidity.get(timezone);
  if (cached) return cached;
  let resolved = 'UTC';
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone });
    resolved = timezone;
  } catch {
    resolved = 'UTC';
  }
  zoneValidity.set(timezone, resolved);
  return resolved;
}

export function formatDate(
  iso: string,
  opts?: Intl.DateTimeFormatOptions,
  timezone?: string
): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: ianaZone(timezone),
    ...opts,
  }).format(new Date(iso));
}

/**
 * Short zone label for an instant, e.g. "UTC", "PDT", "GMT+3". Derived from
 * Intl rather than the stored label, which may be a full IANA id we don't want
 * to print (`America/New_York`) — and which wouldn't reflect DST either way.
 */
export function zoneAbbr(iso: string, timezone?: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: ianaZone(timezone),
      timeZoneName: 'short',
    }).formatToParts(new Date(iso));
    return (
      parts.find((p) => p.type === 'timeZoneName')?.value ?? (timezone ?? 'UTC')
    );
  } catch {
    return timezone ?? 'UTC';
  }
}

export function formatDateTime(iso: string, timezone = 'UTC'): string {
  const tz = ianaZone(timezone);
  const date = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: tz,
  }).format(new Date(iso));
  const time = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: tz,
  }).format(new Date(iso));
  return `${date} · ${time} ${zoneAbbr(iso, timezone)}`;
}

export function formatTime(iso: string, timezone?: string): string {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: ianaZone(timezone),
  }).format(new Date(iso));
}

/** Calendar day (YYYY-MM-DD) of an instant within the given zone. */
export function zonedDayKey(iso: string, timezone?: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: ianaZone(timezone),
  }).format(new Date(iso));
}

/** "in 3 days", "Today", "2 days ago" — compared by calendar day in `timezone`. */
export function relativeDay(
  iso: string,
  timezone?: string,
  now: Date = new Date()
): string {
  const target = zonedDayKey(iso, timezone);
  const today = zonedDayKey(now.toISOString(), timezone);
  const days = Math.round(
    (Date.parse(`${target}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) /
      86_400_000
  );
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  if (days > 0) return `in ${days} days`;
  return `${Math.abs(days)} days ago`;
}

/**
 * Total individual player spots for the event: number of teams × players per
 * team (or just `maxParticipants` for solo events). Registration is per-player,
 * and the first `playerCapacity` registrants get a confirmed spot — everyone
 * after that is a substitute (registration is never capped).
 */
export function playerCapacity(t: Tournament): number {
  return t.maxParticipants * Math.max(1, t.teamSize);
}

/** Whether the unit is teams or players, based on team size. */
export function participantNoun(t: Tournament, plural = true): string {
  const isTeam = t.teamSize > 1;
  if (isTeam) return plural ? 'teams' : 'team';
  return plural ? 'players' : 'player';
}

export function gameIcon(game: string): string {
  return getGameAssetUrl(game);
}

/* ------------------------------------------------------------------ */
/* Game catalogue (for the game selector / per-game lists)             */
/* ------------------------------------------------------------------ */

export interface TournamentGame {
  slug: string;
  name: string;
}

/** Games shown in the selector. */
export const TOURNAMENT_GAMES: TournamentGame[] = [
  { slug: 'apex-legends', name: 'Apex Legends' },
  { slug: 'valorant', name: 'Valorant' },
  { slug: 'league-of-legends', name: 'League of Legends' },
  { slug: 'fortnite', name: 'Fortnite' },
];

/**
 * Per-game tournament rules.
 *
 * Each game has its own idea of what a valid event looks like — a MOBA is
 * always 5v5 on PC, a battle royale is a squad-size choice across every
 * platform. This is the single source of truth the create/manage forms read so
 * an organizer can't build something the game can't actually run (a
 * "Battle Royale" League event, or a PlayStation League event).
 *
 * `teamSizes` lists the selectable squad sizes; a single entry means the size
 * is fixed by the game and the form shows it read-only rather than as an input.
 */
export interface GameTournamentConfig {
  formats: TournamentFormat[];
  platforms: TournamentPlatform[];
  teamSizes: number[];
  defaultFormat: TournamentFormat;
  defaultPlatform: TournamentPlatform;
  defaultTeamSize: number;
  /** Placeholder for the organizer's lobby-code field, per the game's mechanic. */
  lobbyCodePlaceholder: string;
  /** Hard cap on players in one lobby, when the game imposes one. */
  maxLobbyPlayers?: number;
  /**
   * Organizer team map (seat registered players into teams × slots, mirroring
   * the game's private-lobby admin panel). Apex only for now.
   */
  teamMap?: boolean;
}

const BRACKET_FORMATS: TournamentFormat[] = [
  'Single Elimination',
  'Double Elimination',
  'Swiss',
  'Round Robin',
];

export const GAME_TOURNAMENT_CONFIG: Record<string, GameTournamentConfig> = {
  'apex-legends': {
    formats: ['Battle Royale', '1v1', '3v3', ...BRACKET_FORMATS],
    platforms: ['PC', 'PlayStation', 'Xbox', 'Cross-platform'],
    teamSizes: [1, 2, 3],
    defaultFormat: 'Battle Royale',
    defaultPlatform: 'Cross-platform',
    defaultTeamSize: 3,
    lobbyCodePlaceholder: 'e.g. APEX-7741',
    teamMap: true,
  },
  fortnite: {
    // Fortnite names its modes by squad size; brackets are used for custom cups.
    // Ballistic is the exception — a 5v5 tactical mode, and a large share of the
    // Fortnite events both ingesters import are Ballistic cups, so 5 is a real
    // roster size here and not just a bracket shape.
    formats: ['Solo', 'Duos', 'Trios', 'Squads', '5v5', ...BRACKET_FORMATS],
    platforms: ['PC', 'PlayStation', 'Xbox', 'Mobile', 'Cross-platform'],
    teamSizes: [1, 2, 3, 4, 5],
    defaultFormat: 'Duos',
    defaultPlatform: 'Cross-platform',
    defaultTeamSize: 2,
    // Custom matchmaking key: 4-16 chars, no special characters.
    lobbyCodePlaceholder: 'e.g. GPLUGCUP1',
    // Epic caps a custom match at 100 players.
    maxLobbyPlayers: 100,
  },
  'league-of-legends': {
    // Summoner's Rift is 5v5, but 1v1 duels (Howling Abyss / mid-lane
    // first-blood rules) are a long-standing custom-game format, so both the
    // team mode and the bracket shape are selectable.
    formats: ['5v5', '1v1', ...BRACKET_FORMATS],
    platforms: ['PC'],
    teamSizes: [1, 5],
    defaultFormat: '5v5',
    defaultPlatform: 'PC',
    defaultTeamSize: 5,
    // Riot tournament code, pasted into the client's trophy icon.
    lobbyCodePlaceholder: 'e.g. NA1234a-1b2c3d4e',
  },
  valorant: {
    formats: ['5v5', ...BRACKET_FORMATS],
    platforms: ['PC'],
    teamSizes: [5],
    defaultFormat: '5v5',
    defaultPlatform: 'PC',
    defaultTeamSize: 5,
    lobbyCodePlaceholder: 'e.g. GPLUG-VAL1',
  },
};

/** Fallback for games without explicit rules (e.g. an ingested event's game). */
const DEFAULT_GAME_CONFIG: GameTournamentConfig = {
  formats: ['Battle Royale', '1v1', '2v2', '3v3', '5v5', ...BRACKET_FORMATS],
  platforms: ['PC', 'PlayStation', 'Xbox', 'Mobile', 'Cross-platform'],
  teamSizes: [1, 2, 3, 4, 5],
  defaultFormat: 'Single Elimination',
  defaultPlatform: 'PC',
  defaultTeamSize: 1,
  lobbyCodePlaceholder: 'e.g. LOBBY-1234',
};

/** Tournament rules for a game slug, falling back to permissive defaults. */
export function gameTournamentConfig(gameSlug: string): GameTournamentConfig {
  return GAME_TOURNAMENT_CONFIG[gameSlug] ?? DEFAULT_GAME_CONFIG;
}

/** Whether this game fixes team size (so the form shows it read-only). */
/** Whether the organizer manage page offers the team map for this game. */
export function hasTeamMap(gameSlug: string): boolean {
  return gameTournamentConfig(gameSlug).teamMap === true;
}

export function hasFixedTeamSize(gameSlug: string): boolean {
  return gameTournamentConfig(gameSlug).teamSizes.length === 1;
}

/**
 * Validate an event's size against the game's lobby cap (Fortnite: 100
 * players). Returns an organizer-facing message, or null when it fits. Shared
 * by the create and manage forms so both enforce the same rule.
 */
export function lobbyCapError(
  gameSlug: string,
  gameName: string,
  maxParticipants: number,
  teamSize: number
): string | null {
  const cap = gameTournamentConfig(gameSlug).maxLobbyPlayers;
  if (!cap) return null;
  const size = Math.max(1, teamSize);
  if (maxParticipants * size <= cap) return null;
  return `${gameName} lobbies hold ${cap} players — that's ${Math.floor(
    cap / size
  )} teams of ${size}.`;
}

/**
 * Coerce a form's format/platform/teamSize to values the given game allows.
 * Used when the organizer switches games so stale selections (e.g. "Battle
 * Royale" on PlayStation, carried over from Apex to League) can't be saved.
 */
export function coerceToGame(
  gameSlug: string,
  current: {
    format: TournamentFormat;
    platform: TournamentPlatform;
    teamSize: number;
  }
): { format: TournamentFormat; platform: TournamentPlatform; teamSize: number } {
  const cfg = gameTournamentConfig(gameSlug);
  return {
    format: cfg.formats.includes(current.format)
      ? current.format
      : cfg.defaultFormat,
    platform: cfg.platforms.includes(current.platform)
      ? current.platform
      : cfg.defaultPlatform,
    teamSize: cfg.teamSizes.includes(current.teamSize)
      ? current.teamSize
      : cfg.defaultTeamSize,
  };
}

// Legacy: tournaments whose Overstat match id was hard-coded before organizers
// could attach a link themselves. The DB column `tournaments.overstat_match_id`
// is the source of truth now (backfilled with these values in migration
// 20260826000000); this map is only a fallback for rows that predate it.
export const TOURNAMENT_RESULTS_MATCH_ID: Record<string, string> = {
  'gamerplug-x-dbellguns': '20944',
  'gamerplug-x-luckyshots': '20941',
  'gamerplug-x-coachfrosty': '20945',
  'gamerplug-creators-finals': '21036',
  'gamerplug-creators-finals-22b40': '21771',
  'edboigaming-x-gamerplug-community-tournament-c4d04': '21951',
  'community-double-header-with-luckyshots-x-dbellguns-50eb9': '22507',
  'community-double-header-with-edboigaming-x-ireekofawesome-96e9b': '22508',
}

/** Overstat match id for a tournament slug, or null if we don't host its results. */
export function resultsMatchIdForSlug(slug: string): string | null {
  return TOURNAMENT_RESULTS_MATCH_ID[slug] ?? null
}

/**
 * Overstat match id for a tournament: the organizer-set column first, then the
 * legacy slug map. Null means there is no results page for this event.
 */
export function resultsMatchIdFor(
  t: Pick<Tournament, 'slug' | 'overstatMatchId'>
): string | null {
  return t.overstatMatchId?.trim() || resultsMatchIdForSlug(t.slug)
}

/**
 * Pull the numeric match id out of anything an organizer might paste from
 * Overstat: a tournament page (`overstat.gg/tournament/<org>/<id>.<name>/...`),
 * an API url (`overstat.gg/api/stats/<id>/overall`), or the bare id.
 * Returns null when nothing usable is found.
 */
export function parseOverstatMatchId(input: string): string | null {
  const raw = input.trim()
  if (!raw) return null
  if (/^\d+$/.test(raw)) return raw

  let path: string
  try {
    const url = new URL(raw.includes('://') ? raw : `https://${raw}`)
    if (!/(^|\.)overstat\.gg$/i.test(url.hostname)) return null
    path = url.pathname
  } catch {
    return null
  }
  // First path segment that starts with digits (optionally followed by `.name`).
  for (const seg of path.split('/')) {
    const m = /^(\d+)(?:\.|$)/.exec(decodeURIComponent(seg))
    if (m) return m[1]
  }
  return null
}

export type CtaKind = 'join' | 'external' | 'waitlist' | 'results'

/** Status- and registration-aware primary call to action for the detail page. */
/** Created on GamerPlug (not imported from Battlefy, Challengermode, …). */
export function isGamerPlugHosted(t: Pick<Tournament, 'source'>): boolean {
  return !t.source
}

const SOURCE_LABELS: Record<string, string> = {
  battlefy: 'Battlefy',
  challengermode: 'Challengermode',
}

/** Display name of an import source, e.g. 'challengermode' → 'Challengermode'. */
export function tournamentSourceLabel(source: string): string {
  return SOURCE_LABELS[source] ?? source.charAt(0).toUpperCase() + source.slice(1)
}

export type TimeFilter = 'upcoming' | 'past'

/**
 * The Upcoming / Past split both tournament pages use. By the event's calendar
 * day, so an event that already started today still counts as upcoming, except
 * completed events, which always belong to Past. Upcoming sorts soonest first,
 * Past most recent first.
 */
export function filterByTime<T extends Pick<Tournament, 'status' | 'startsAt'>>(
  list: T[],
  time: TimeFilter,
  now: Date = new Date()
): T[] {
  const startOfToday = new Date(now)
  startOfToday.setHours(0, 0, 0, 0)
  const boundary = startOfToday.getTime()
  const isUpcoming = (t: T) => t.status !== 'completed' && new Date(t.startsAt).getTime() >= boundary
  return list
    .filter((t) => (time === 'upcoming' ? isUpcoming(t) : !isUpcoming(t)))
    .sort((a, b) => {
      const da = new Date(a.startsAt).getTime()
      const db = new Date(b.startsAt).getTime()
      return time === 'upcoming' ? da - db : db - da
    })
}

/** GamerPlug-hosted events before imported ones; keeps the given order otherwise. */
export function gamerPlugFirst(a: Pick<Tournament, 'source'>, b: Pick<Tournament, 'source'>): number {
  return Number(!isGamerPlugHosted(a)) - Number(!isGamerPlugHosted(b))
}

export function getPrimaryCta(t: Tournament): { kind: CtaKind; label: string } {
  if (t.status === 'completed') return { kind: 'results', label: 'View Results' }
  if (t.externalRegistrationUrl)
    return { kind: 'external', label: 'Register on Organizer Site' }
  // Registration is never capped — anyone can join. Players beyond the
  // confirmed-spot capacity become substitutes (handled at the call site).
  return { kind: 'join', label: 'Join Tournament' }
}

/** Whether the registration deadline has passed relative to `now`. */
export function registrationClosed(t: Tournament, now: Date = new Date()): boolean {
  return new Date(t.registrationDeadline).getTime() < now.getTime()
}

export const STATUS_FILTERS: { value: TournamentStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'live', label: 'Live' },
  { value: 'full', label: 'Full' },
  { value: 'completed', label: 'Completed' },
];
